import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createBlacklistFilter } from 'redux-persist-transform-filter';
import { RootState } from '../../../store';

/**
 * GridCell — a pre-aggregated territory cell computed by the aggregateTileWrite CF.
 * The cell covers a square region of `resolution` degrees.
 *
 * Bounds are NOT stored here — they are computed client-side from cellId + resolution:
 *   center = parseFloat(cellId.split(':')[0 | 1])
 *   bounds = { north: center + resolution/2, south: center - resolution/2, ... }
 */
export interface GridCell {
    /** "{roundedLng}:{roundedLat}" — center of cell */
    cellId: string;
    /** Geohash of cell center (for Firestore spatial queries) */
    geohash: string;
    /** Hex string of dominant color e.g. "#ff0000" */
    dominantColor: string;
    /** Count per color hex string e.g. { "#ff0000": 42 } */
    colorCounts: { [hexColor: string]: number };
}

export interface CellsState {
    /**
     * Fine cells (0.01° resolution, ~1.1km) — served at zoom 8–15.
     * Split from coarse so TerritoryLayer can render each with independent
     * Mapbox zoom-based visibility without cells mixing between resolutions.
     */
    fine: { [cellId: string]: GridCell };
    /** Coarse cells (0.1° resolution, ~11km) — served at zoom 0–8. */
    coarse: { [cellId: string]: GridCell };
    isLoading: boolean;
}

const initialState: CellsState = {
    fine: {},
    coarse: {},
    isLoading: false,
};

export const sliceName = 'cells';

// Cell data is always fresh from Firestore — never persist it in localStorage.
export const persistFilter = createBlacklistFilter(sliceName, ['fine', 'coarse']);

export const selectFineCells   = (state: RootState) => state.cells.fine;
export const selectCoarseCells = (state: RootState) => state.cells.coarse;
export const selectCellsLoading = (state: RootState) => state.cells.isLoading;

export const cellsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        /** Merge a batch of fine cells into state.fine */
        setFineCells(state, action: PayloadAction<GridCell[]>) {
            for (const cell of action.payload) {
                state.fine[cell.cellId] = cell;
            }
        },
        /** Merge a batch of coarse cells into state.coarse */
        setCoarseCells(state, action: PayloadAction<GridCell[]>) {
            for (const cell of action.payload) {
                state.coarse[cell.cellId] = cell;
            }
        },
        /** Wipe both fine and coarse cell maps (e.g. on sign-out or full reset) */
        clearCells(state) {
            state.fine = {};
            state.coarse = {};
        },
        /** Remove a single cell document (triggered by CF deleting an empty cell) */
        removeCell(state, action: PayloadAction<{ resolution: 'fine' | 'coarse'; cellId: string }>) {
            const { resolution, cellId } = action.payload;
            delete state[resolution][cellId];
        },
        setCellsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
    },
});

export const {
    setFineCells,
    setCoarseCells,
    clearCells,
    removeCell,
    setCellsLoading,
} = cellsSlice.actions;

export default cellsSlice.reducer;
